import React from 'react';
import axios from 'axios';
import { getIn, useFormikContext } from 'formik';
import { isEmpty, reduce } from 'lodash';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import UpsertContactFormik from '~/components/Contacts/UpsertContact/UpsertContactFormik';
import { useContactTabs } from '~/components/Contacts/UpsertContact/useContactTabs';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import { useCms } from '~/components/hooks/useCms';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

import type { InitialValuesType, SpecialRoleBehaviorType } from '../types';
import { ContactContextProvider } from '../useContact';

import { splitCreateContactPayload, stripDeadCreateContactKeys } from './utils';

interface CreateContactDialogInnerPropsType {
  onClose: () => void;
  title?: string;
}

interface CreateContactPropsType extends CreateContactDialogInnerPropsType {
  onContactCreate: (contact: ContactFullModel) => Promise<void>;
  initialValues?: InitialValuesType;
}

interface CreateContactWrapperPropsType extends CreateContactPropsType, SpecialRoleBehaviorType {}

const CreateContactDialogInner: React.FC<CreateContactDialogInnerPropsType> = ({
  onClose,
  title = 'Create Contact',
}) => {
  const { isSubmitting, handleSubmit, errors, touched } = useFormikContext();
  const { filteredTabs } = useContactTabs();

  const tabHasErrors = (tabKey: string) =>
    !isEmpty(getIn(errors, tabKey)) &&
    getIn(touched, tabKey) &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    Object.keys(errors[tabKey]).some((i) => Object.keys(touched[tabKey]).includes(i));

  const tabs = filteredTabs.map((tab) => ({
    hasErrors: tabHasErrors(tab.tabKey),
    label: tab.label,
    component: tab.tabComponent,
  }));

  return (
    <CardDialog isDialog title={title} onClose={onClose} maxWidth="md" fullWidth>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <div className="my-20 max-h-[600px] overflow-scroll">
        {tabs.map((tab) => {
          const Component = tab.component;
          return (
            <CollapsibleWrapper
              key={tab.label}
              title={tab.label}
              actionCard
              noDivider
              defaultState
              backgroundColor={CollapsibleWrapper.BACKGROUND_COLOR.WHITE}
              titleColor={
                tab.hasErrors ? CollapsibleWrapper.TITLE_COLOR.error : CollapsibleWrapper.TITLE_COLOR.textPrimary
              }
            >
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <Component />
            </CollapsibleWrapper>
          );
        })}
      </div>
      <div className="mt-px flex w-full justify-end">
        <CancelButton disabled={isSubmitting} onClick={onClose} />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
          Create
        </Button>
      </div>
    </CardDialog>
  );
};

const CreateContactFormik: React.FC<CreateContactPropsType> = ({ onClose, onContactCreate, title, initialValues }) => {
  const { claim } = useClaim();
  const { userOrganization } = useCms();
  const { allTabs } = useContactTabs();

  const handlePreparePayload = (payload: Record<string, unknown>) => {
    const flattened = reduce(
      allTabs,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      (result, tab) => {
        if (payload[tab.tabKey]) {
          const tabPayload = payload[tab.tabKey] as Record<string, unknown>;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          delete result[tab.tabKey];
          return { ...result, ...tabPayload };
        }
        return result;
      },
      { ...payload, organization_id: userOrganization.id }
    );
    return stripDeadCreateContactKeys(flattened);
  };

  const handleSubmit = async (payload: Record<string, unknown>) => {
    const { contactPayload, exposureIds } = splitCreateContactPayload(payload);
    const response = await axios.post(`/api/v1/organizations/${userOrganization.id}/contacts`, contactPayload);

    if (claim) {
      await axios.post(`/api/v1/organizations/${userOrganization.id}/claim_contacts/claims/${claim.id}/contacts`, {
        contact_id: response.data.id,
        exposure_ids: exposureIds,
      });
    }

    await onContactCreate(response.data);
  };

  return (
    <UpsertContactFormik
      onClose={onClose}
      onSubmit={handleSubmit}
      onPreparePayload={handlePreparePayload}
      initialValues={initialValues}
      dialogInnerComponent={<CreateContactDialogInner onClose={onClose} title={title} />}
    />
  );
};

const CreateContactDialog: React.FC<CreateContactWrapperPropsType> = ({
  acceptedRoles,
  editingRoleDisabled,
  ...restProps
}) => {
  return (
    <ContactContextProvider specialRoleBehavior={{ acceptedRoles, editingRoleDisabled }}>
      <CreateContactFormik {...restProps} />
    </ContactContextProvider>
  );
};

export { CreateContactDialog };
